import { useCallback, useEffect, useState, useMemo } from 'react';
import { abortable } from '@entegral/helpers.react/async/abortable';
import { createApi } from '@entegral/helpers.axios';
import { delay } from '@entegral/helpers.react/async/delay';
import  composeUrl from '@entegral/helpers.formatters/url/compose';
import { useSearchCache } from './search-cache';

let API = createApi(`https://apis.entegral.net`);

export function useSearch({
  fitBounds,
  filters,
  isFiltersOpened,
  isFiltersInited,
  urlFormat,
}) {
  let [ properties, setProperties ] = useState([]);
  let [ count, setCount ] = useState(0);
  let [ isLoading, setLoading ] = useState(true);
  let [ hash, setHash ] = useState('');
  let { getFromCache, putIntoCache, isCacheReady } = useSearchCache();

  urlFormat = useMemo(() => urlFormat.replace(`{{propertyType}}`, `{{type}}`).replace(`{{propertyid}}`, `{{id}}`), [urlFormat]);

  let setPropertiesAndBounds = useCallback(properties => {
    setProperties(properties);
    if (properties.length) fitBounds(properties.map(x => x.coords));
  }, [fitBounds]);

  useEffect(() => {
    if (isFiltersOpened || !isFiltersInited || !isCacheReady) return;

    let normilizedFilters = { ...filters };

    for (let key in filters) {
      if (filters[key] === null) {
        delete normilizedFilters[key];
      }
    }

    let newHash = JSON.stringify(normilizedFilters);
    if (newHash === hash) return;

    let cache = getFromCache(newHash);

    if (cache) {
      setPropertiesAndBounds(cache.properties);
      setCount(cache.count);
      setHash(newHash);
      setLoading(false);
      return;
    }

    setProperties([]);
    setCount(0);

    return abortable(async (isAborted) => {
      await delay(50);

      if (isAborted()) return;

      setLoading(true);

      let { listings: properties, count } = await API.post(`/search`, normilizedFilters);

      putIntoCache(newHash, { properties, count });

      if (isAborted()) return;

      setPropertiesAndBounds(properties.map(x => propertyMapper(x, urlFormat)));
      setCount(count);
      setHash(newHash);
      setLoading(false);
    });
  }, [filters, isFiltersOpened, isFiltersInited, isCacheReady]);

  return {
    properties,
    count,
    isLoading,
  };
}

function propertyMapper(property, urlFormat) {
  property.coords = [property.coords.lat, property.coords.lon];
  property.coords[0] = parseFloat(property.coords[0].toFixed(4));
  property.coords[1] = parseFloat(property.coords[1].toFixed(4));

  if (property.status === `For Sale`) {
    property.statusUrl = `for sale`;
  } else if (property.term === `Monthly`) {
    property.statusUrl = `rental monthly`;
  } else {
    property.statusUrl = `rental daily`;
  }

  property.url = composeUrl(property, urlFormat);

  return property;
}
